import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useAtom } from 'jotai'
import { useAtomValue } from 'jotai'
import { useCallback } from 'react'
import * as React from 'react'
import { useNavigate } from 'react-router'

import GroupSessionForm from './GroupSessionForm'
import {
  groupSessionCreateFormAtom,
  groupSessionCreateErrorAtom,
  groupSessionCreateSubmitAtom,
  groupSessionCreateResetAtom,
  groupSessionCreateFormKeyAtom,
} from '../../contexts/AtomGroupSessionCreate'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import GroupSessionService from '../../services/GroupSessionService'
import { isInstructor } from '../../share/utility'
import useBar from '../common/Bar'

export default function GroupSessionCreate() {
  const navigate = useNavigate()
  const user = useAtomValue(currentUserAtom)

  const bar = useBar()
  const [formData, setFormData] = useAtom(groupSessionCreateFormAtom)
  const [formErrors, setFormErrors] = useAtom(groupSessionCreateErrorAtom)
  const [submitting, setSubmitting] = useAtom(groupSessionCreateSubmitAtom)
  const [, resetForm] = useAtom(groupSessionCreateResetAtom)
  const [formKey, setFormKey] = useAtom(groupSessionCreateFormKeyAtom)

  // Dialog state for "Use Previous Session"
  const [usePrevOpen, setUsePrevOpen] = React.useState(false)
  const [loadingPrev, setLoadingPrev] = React.useState(false)
  const [prevSessions, setPrevSessions] = React.useState([])

  const openUsePreviousDialog = async () => {
    setUsePrevOpen(true)
    setLoadingPrev(true)
    try {
      const sessions = await GroupSessionService.getGroupSessions({
        instructorId: user.resourceId,
        size: -1,
      })
      setPrevSessions(sessions || [])
      if (!sessions || sessions.length === 0) {
        bar.warn('No previous sessions found')
      }
    } catch (err) {
      console.error('Failed to fetch previous sessions:', err)
      bar.error(err.message || 'Failed to load previous sessions')
    } finally {
      setLoadingPrev(false)
    }
  }

  const closeUsePreviousDialog = () => setUsePrevOpen(false)

  const toHHMMSS = (value) => {
    try {
      if (!value) return value
      if (typeof value === 'string' && value.includes('T')) {
        const d = new Date(value)
        const hh = String(d.getHours()).padStart(2, '0')
        const mm = String(d.getMinutes()).padStart(2, '0')
        const ss = String(d.getSeconds()).padStart(2, '0')
        return `${hh}:${mm}:${ss}`
      }
      return value
    } catch {
      return value
    }
  }

  const computeHoursDiff = (dateISO, deadlineISO, fallbackHours) => {
    try {
      if (!dateISO || !deadlineISO) return fallbackHours
      const diffMs = new Date(dateISO).getTime() - new Date(deadlineISO).getTime()
      const hours = Math.round(diffMs / (1000 * 60 * 60))
      return Number.isFinite(hours) ? Math.max(hours, 0) : fallbackHours
    } catch {
      return fallbackHours
    }
  }

  const handlePickPrevious = (session) => {
    const mapped = {
      ...formData,
      title: session.title || '',
      description: session.description || '',
      details: session.details || '',
      sport: session.sport || '',
      skiResort: session.skiResort || '',
      date: session.date || formData.date,
      times: Array.isArray(session.times)
        ? session.times.map((t) => ({
            startTime: toHHMMSS(t.startTime || t.start || t.start_time),
            endTime: toHHMMSS(t.endTime || t.end || t.end_time),
          }))
        : [],
      price: session.price ?? formData.price,
      minParticipants: session.minParticipants ?? formData.minParticipants,
      maxParticipants: session.maxParticipants ?? formData.maxParticipants,
      participantIds: [],
      useAbsolute: true,
      registrationDeadlineHours: computeHoursDiff(session.date, session.registrationDeadline, 24),
      cancellationDeadlineHours: computeHoursDiff(session.date, session.cancellationDeadline, 12),
      registrationDeadline: undefined,
      cancellationDeadline: undefined,
      pictures: session.pictures || [],
    }
    setFormData(mapped)
    setFormKey(formKey + 1)
    bar.info('Loaded previous session content')
    closeUsePreviousDialog()
  }

  const onFormChange = useCallback(
    (data) => {
      setFormData((prev) => {
        return { ...prev, ...data }
      })
    },
    [setFormData],
  )

  // Check user permissions
  React.useEffect(() => {
    if (!isInstructor(user)) {
      bar.error('You do not have permission to create group sessions')
      navigate('/')
    }
  }, [user, navigate, bar])

  const validateForm = (data) => {
    const errors = {}
    if (!data.title) errors.title = 'Please enter a title'
    if (!data.sport) errors.sport = 'Please select a sport'
    if (!data.skiResort) errors.skiResort = 'Please select a ski resort'
    if (!data.price || data.price < 0) errors.price = 'Please enter a valid price'
    if (!data.minParticipants || data.minParticipants < 1) errors.minParticipants = 'Minimum participants must be greater than 0'
    if (!data.maxParticipants || data.maxParticipants < data.minParticipants) {
      errors.maxParticipants = 'Maximum participants must be greater than minimum participants'
    }
    if (!data.date) errors.date = 'Please select a date'
    else {
      const todayStart = dayjs().startOf('day')
      const sessionDate = dayjs(data.date).startOf('day')
      if (sessionDate.isBefore(todayStart)) {
        errors.date = 'Session date cannot be in the past'
      }
    }

    // Validate that each time slot has start and end times
    if (data.times) {
      data.times.forEach((timeSlot, index) => {
        if (!timeSlot.startTime || !timeSlot.endTime) {
          errors[`timeSlot${index}`] = 'Please complete time slot information'
        }
      })
    }

    if (!errors.date) {
      // Validate registration deadline is not in the past
      const now = dayjs()
      if (data.useAbsolute) {
        if (data.registrationDeadlineHours == null || Number(data.registrationDeadlineHours) < 0) {
          errors.registrationDeadlineHours = 'Please provide a valid registration deadline (hours before)'
        } else if (data.date) {
          const registrationDeadlineComputed = dayjs(data.date).subtract(Number(data.registrationDeadlineHours), 'hour')
          if (registrationDeadlineComputed.isBefore(now)) {
            errors.registrationDeadlineHours = 'Registration deadline has already passed'
          }
        }
      } else {
        if (!data.registrationDeadline) {
          errors.registrationDeadline = 'Please select a registration deadline'
        } else {
          const registrationDeadlineAbs = dayjs(data.registrationDeadline)
          if (registrationDeadlineAbs.isBefore(now)) {
            errors.registrationDeadline = 'Registration deadline has already passed'
          }
        }
      }
    }

    return errors
  }

  const handleSubmit = async () => {
    // TODO better error indicator when create session
    const errors = validateForm(formData)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      bar.error('Please fix form errors before submitting')
      return
    }

    try {
      setSubmitting(true)
      const sessionData = {
        ...formData,
        instructorId: user.resourceId,
      }
      await GroupSessionService.createGroupSession(sessionData)
      bar.success('Session created successfully')
      resetForm()
      navigate('/events/sessions')
    } catch (err) {
      console.error('Failed to create session:', err)
      bar.error(err.message || 'Failed to create session')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Create Group Session
      </Typography>

      <GroupSessionForm key={formKey} formData={formData} formErrors={formErrors} onChange={onFormChange} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ mt: 4 }}>
        <Tooltip title="Cancel and return to Sessions">
          <span>
            <Button
              variant="outlined"
              onClick={() => {
                navigate('/events/sessions')
              }}
              disabled={submitting}
              aria-label="Cancel and return to Sessions"
            >
              Cancel
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Prefill the form from one of your previous sessions">
          <span>
            <Button variant="outlined" onClick={openUsePreviousDialog} disabled={submitting} aria-label="Use previous session to prefill form">
              Use Previous Session
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Clear all inputs and restore default values">
          <span>
            <Button
              variant="outlined"
              onClick={() => {
                resetForm()
                bar.info('Form reset to default settings')
              }}
              disabled={submitting}
              aria-label="Reset form to default values"
            >
              Reset
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Create this session with the current details">
          <span>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ minWidth: 120 }} aria-label="Create session">
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Create Session'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Dialog open={usePrevOpen} onClose={closeUsePreviousDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Select a previous session</DialogTitle>
        <DialogContent>
          {loadingPrev ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List>
              {prevSessions.map((s) => {
                const id = s.resourceId || s.id
                const secondary = [s.sport, s.skiResort, s.date].filter(Boolean).join(' • ')
                return (
                  <ListItem key={id} disablePadding>
                    <ListItemButton onClick={() => handlePickPrevious(s)}>
                      <ListItemText primary={s.title || 'Untitled Session'} secondary={secondary} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
              {prevSessions.length === 0 && (
                <ListItem>
                  <ListItemText primary="No previous sessions" secondary="You have no sessions to copy from" />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  )
}
